import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/school-football-markers.jpg";
import heroPicture from "@/assets/school-football-markers.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import schoolTennis from "@/assets/school-tennis-court.jpg";
import schoolHighJump from "@/assets/school-high-jump.jpg";
import schoolGoalposts from "@/assets/school-goalposts.jpg";
import schoolFootball from "@/assets/school-football-markers.jpg";
import brochure from "@/assets/schools-athletics-brochure.png";
import wyattBanner from "@/assets/wyatt-banner-la-vernia.jpg";
import playgroundRopePads from "@/assets/playground-rope-climber-padded-anchors-serge-ferrari.jpg";

const QUOTE_HREF = `/#lets-talk?type=${encodeURIComponent("School / Athletic Field Cover")}&notes=${encodeURIComponent(
  "School / Athletic Department inquiry — please include school name, item(s) needed (e.g. tennis windscreens, high-jump pit cover, goalpost pads, sideline markers), approximate sizes, and color preferences."
)}`;

export const Route = createFileRoute("/schools-athletics")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "School & Athletic Field Covers, Pads & Windscreens — Texas | Texas Made Shade",
      description:
        "Custom school and athletic field covers, padded goalpost wraps, high-jump and pole-vault pit covers.",
      image: hero,
      imageAlt:
        "Custom-sewn football sideline yard markers and athletic field covers for a Texas school district",
      socialTitle:
        "Custom School & Athletic Field Covers, Pads & Windscreens — Texas",
      socialDescription:
        "Tennis windscreens, pit pads, padded goalpost covers and sideline gear covers — built to district spec in Seguin, TX.",
      keywords:
        "school athletic field covers, athletic field pad covers, tennis court windscreens, high jump pit cover, pole vault pit cover, padded goalpost covers, gym wall pads, sideline yard markers, blocking sled covers, custom school covers Texas, athletic department covers, school stadium covers, Texas Made Shade",
    }),
    scripts: serviceHeadScripts({
      path: "/schools-athletics",
      serviceName: "School & Athletic Field Covers",
      serviceType: "Custom School & Athletic Department Covers",
      description: "Custom school and athletic field covers, padded goalpost wraps, high-jump and pole-vault pit covers, tennis windscreens and sideline gear covers — built to district spec in Seguin, TX.",
      faqs: [
        { q: "Do you build custom covers for Texas school districts and universities?", a: "Yes — we build athletic field covers, padded goalpost wraps, tennis windscreens, high-jump and pole-vault pit covers, sideline markers and equipment covers for school districts, universities and youth sports programs across Texas." },
        { q: "Can you match our school colors and print our logo?", a: "Yes. We stock heavy marine-grade vinyl and Sunbrella fabrics in a wide range of school colors and can custom-print logos and lettering on windscreens, goalpost wraps, sideline markers and equipment covers." },
        { q: "Do you submit quotes for school district purchase orders?", a: "Yes — we work directly with athletic directors, facility managers and district purchasing departments. Send us specs and we'll send a written quote you can drop into a PO." },
      ],
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/schools-athletics"
      serviceName="School & Athletic Covers"
      breadcrumbs={[{ label: "Schools & Athletics" }]}
      eyebrow="★ SCHOOLS & ATHLETICS ★"
      title="Custom School & Athletic Field Covers, Pads & Windscreens — Texas"
      intro="Texas Made Shade builds custom athletic field covers, padded equipment shrouds and protective windscreens for school districts, universities and youth sports programs across Central, South and Coastal Texas. Every cover is patterned to your equipment and stitched in heavy marine-grade fabric to stand up to UV, rain and field-day abuse."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "School & Athletic Department Quotes · Seguin, TX",
        headline: "Need Custom Covers for Your School or Athletic Department?",
        subhead:
          "Tennis windscreens, pit pads, goalpost covers, sideline markers and field equipment covers — built to district spec. Tell us what you need and we'll send a free quote.",
        primaryHref: QUOTE_HREF,
        primaryLabel: "Get a Free School Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Tennis court windscreens with custom-printed school logos",
        "Weatherproof high-jump and pole-vault pit covers",
        "Impact-rated padded goalpost wraps and gym wall pads",
        "Sideline yard markers, blocking sled covers and field tarps",
        "Heavy marine-grade vinyl and Sunbrella fabrics in school colors",
        "Built to district spec in our Seguin, TX canvas shop",
      ]}
      body={[
        {
          heading: "Schools & Sports Capabilities at a Glance",
          text: "From shade panels and tension structures to tennis windscreens, custom awnings, wall and pole padding, and fully custom-sewn projects — here's an overview of what we build for Texas schools and athletic departments.",
          image: { src: brochure, alt: "Texas Made Shade & Covers brochure showing services for schools and sports: shade panels, tension structures, awnings, canopies, netting, tennis curtains, windscreens, custom sewn projects, and wall and pole padding" },
        },
        {
          heading: "★ What Texas Athletic Directors Say",
          text: "\"Texas Made Shade re-skinned every windscreen on our tennis courts and built new pit covers for track. Three seasons in and they still look new. Their quotes drop straight into a PO and they hit our delivery dates — that matters when you're trying to get a field ready before the first home meet.\" — Athletic Director, Central Texas 5A ISD",
        },
        {
          heading: "Tennis Court Windscreens & Privacy Wraps",
          text: "Heavy vinyl-coated polyester windscreens cut wind, glare and visual distractions on school tennis courts. Custom-printed school logos and reinforced grommets available — sized to your fence runs and finished to handle Texas sun and storm cells.",
          image: { src: schoolTennis, alt: "Custom tennis court windscreens installed at a Texas high school" },
        },
        {
          heading: "High-Jump & Pole Vault Pit Covers",
          text: "Weatherproof track and field pit covers protect landing pads from sun and rain between meets — extending pad life, preventing mildew and saving the athletic department thousands on replacement foam.",
          image: { src: schoolHighJump, alt: "Heavy-duty high jump pit cover for a school track and field program" },
        },
        {
          heading: "Padded Goalpost Covers & Wall Pads",
          text: "Impact-rated padded goalpost wraps, end-zone pylons and gym wall pads built to your school colors with vinyl that wipes clean. Safe for athletes, branded for your program.",
          image: { src: schoolGoalposts, alt: "Padded football goalpost covers for a Texas school stadium" },
        },
        {
          heading: "Playground Rope-Climber & Anchor Pads — Serge Ferrari® Fabric",
          text: "Custom-sewn padded anchor covers and impact pads for school and park playground rope-climbers, web structures and net climbers. Built from Serge Ferrari® precontraint fabric — the same high-tenacity coated mesh used on tension structures and stadium canopies — for UV stability, tear strength and a wipe-clean surface that holds up to daily recess use, mulch, sun and Texas weather. Patterned to your exact climber hardware in school or park colors.",
          image: { src: playgroundRopePads, alt: "Custom teal Serge Ferrari fabric padded anchor covers fabricated by Texas Made Shade and installed on a playground rope-climber web structure outside a Texas school" },
        },
        {
          heading: "Sideline Markers & Field Equipment Covers",
          text: "Custom yard markers, blocking sled covers and tarps for football, baseball and soccer programs — patterned and sewn in our Seguin shop in your school's colors and lettering.",
          image: { src: schoolFootball, alt: "Custom football sideline yard markers for a school athletic department" },
        },
        {
          heading: "Branded School & District Awnings",
          text: "Branded entry awnings and storefront banners for school districts, athletic facilities and booster-club buildings — welded aluminum frames with Sunbrella canvas in your school colors and lettering.",
          image: { src: wyattBanner, alt: "Custom commercial banner awning fabricated and installed by Texas Made Shade over the Wyatt's storefront entrance in La Vernia, Texas — La Vernia ISD area branded awning project", caption: "Wyatt's · La Vernia, TX — La Vernia ISD area" },
        },
      ]}
      testimonials={{
        eyebrow: "School & Athletic Department Reviews · 5.0 Rating",
        title: "Trusted by Texas Athletic Directors & Facility Managers",
        items: [
          {
            quote: "Our high-jump and pole-vault pit covers from Texas Made Shade have already paid for themselves twice over in pad life. Stitching, grommets and fit are bulletproof — and they handled the PO process with zero friction.",
            author: "Athletic Director",
            role: "Track & Field Pit Covers",
            location: "Central Texas 5A ISD",
            rating: 5,
          },
          {
            quote: "We needed branded windscreens for the entire tennis complex and padded goalpost wraps before the first home football game. They patterned, printed and installed everything on schedule and in our exact school colors. Game-day ready.",
            author: "Facility Manager",
            role: "Tennis Windscreens + Goalpost Pads",
            location: "South Texas High School",
            rating: 5,
          },
          {
            quote: "Best vendor we've used for athletic covers, hands down. The quote came back same week, the build matched spec, and the install crew was professional. We'll be using them for sideline markers and blocking sled covers next season.",
            author: "Head Football Coach / AD",
            role: "Sideline Markers & Sled Covers",
            location: "Hill Country ISD",
            rating: 5,
          },
          {
            quote: "Custom gym wall pads and end-zone pylons in our school colors — perfect fit, safe for the kids, and they look sharp on game film. Easy to work with and they understand school district purchasing.",
            author: "Athletic Coordinator",
            role: "Gym Wall Pads & Pylons",
            location: "Coastal Texas District",
            rating: 5,
          },
        ],
      }}
      applicationsEyebrow="WHAT WE BUILD FOR SCHOOLS"
      applicationsHeading="Custom School & Athletic Department Products"
      applications={[
        "Tennis Court Windscreens",
        "High Jump Pit Covers",
        "Pole Vault Pit Covers",
        "Padded Goalpost Wraps",
        "End-Zone Pylons",
        "Gym Wall Pads",
        "Sideline Yard Markers",
        "Blocking Sled Covers",
        "Baseball Field Tarps",
        "Soccer Goal Covers",
        "Playground Rope-Climber Pads",
        "Equipment & Booster Trailer Covers",
        "Branded Entry Awnings",
      ]}
      faqs={[
        {
          q: "Do you build custom covers for Texas school districts and universities?",
          a: "Yes — we build athletic field covers, padded goalpost wraps, tennis windscreens, high-jump and pole-vault pit covers, sideline markers and equipment covers for school districts, universities and youth sports programs across Texas. Everything is patterned and sewn in our Seguin shop.",
        },
        {
          q: "Can you match our school colors and print our logo?",
          a: "Yes. We stock heavy marine-grade vinyl and Sunbrella fabrics in a wide range of school colors and we can custom-print logos and lettering on tennis windscreens, goalpost wraps, sideline markers and equipment covers.",
        },
        {
          q: "Do you submit quotes for school district purchase orders?",
          a: "Yes — we work directly with athletic directors, facility managers and district purchasing departments. Send us your specs and we'll send a written quote you can drop into a PO.",
        },
      ]}
    />
  ),
});
